import { useState, useContext, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import MenuIcon from '@mui/icons-material/Menu'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import ListSubheader from '@mui/material/ListSubheader'

import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'

import { AppContext } from "/providers/app-context"
import logo from '/assets/images/smartix-logo.png'

export default function ButtonAppBar({ title, toggleHelp, toggleSettings, isErrorPage }) {

  const navigate = useNavigate()

  const [anchorEl, setAnchorEl] = useState(null)
  const { client, creditBalance, selectedClientId, selectClient } = useContext(AppContext)
  const availableClients = client?.available_clients ?? []
  const parentClientId = client?.client_id?.toString()
  const parentClient = availableClients.find(item => item.client_id?.toString() === parentClientId)
  const childClients = availableClients.filter(item => item.client_id?.toString() !== parentClientId)
  const selectedClient = availableClients.find(item => item.client_id?.toString() === selectedClientId?.toString())
  const showClientSelector = availableClients.length > 1
  const showCreditBalance = client?.billing_mode === "prepay" && creditBalance

  const menuOpen = Boolean(anchorEl)
  const handleClick = e => setAnchorEl(e.currentTarget)
  const handleClose = () => setAnchorEl(null)

  useEffect(() => {
    document.title = "Smartix: " + title
  }, [title])


  return (
    <Box >
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 , bgcolor:"menubar.bg", color:"menubar.fg"}}>
        <Toolbar>
          {!isErrorPage &&
            <>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={handleClick}
                disabled={client === null}
              >
                <MenuIcon />
              </IconButton>

              <Menu
                id="basic-menu"
                open={menuOpen}
                onClose={handleClose}
                anchorEl={anchorEl}
              >
                <MenuItems handleClose={handleClose} />
              </Menu>
            </>}

          <img src={logo} style={{ height: 50, marginRight: 10 }} />

          <Typography variant="h6" component="div" sx={{ flexGrow: 1, flexShrink: 0, whitespace: "nowrap" }}>
            <span className='smartix'>SMARTIX</span> <span className='studio'>Studio</span> : {title ?? ""}
          </Typography>

          {showCreditBalance ? (
            <Button
              onClick={() => navigate("/billing/")}
              variant="outlined"
              sx={{mr:5}}
            >
              Credit balance: {parseInt(creditBalance.credits ?? 0, 10).toLocaleString()}
            </Button>
          ) : null}

          {showClientSelector ? (
            <Select
              size='small'
              value={selectedClientId?.toString() ?? ''}
              onChange={e => selectClient(e.target.value)}
              renderValue={() => {
                if (!selectedClient) return ''
                const scopeLabel = selectedClient.client_id?.toString() === parentClientId ? 'Parent' : 'Child'
                return `${scopeLabel}: ${selectedClient.name || "Unnamed client"} (${selectedClient.client_id})`
              }}
              sx={{
                mr: 2,
                minWidth: 320,
                color: 'inherit',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'currentColor' },
                '& .MuiSvgIcon-root': { color: 'inherit' }
              }}
            >
              {parentClient ? <ListSubheader>Parent client</ListSubheader> : null}
              {parentClient ? (
                <MenuItem key={parentClient.client_id} value={parentClient.client_id.toString()}>
                  {parentClient.name || "Unnamed client"} ({parentClient.client_id})
                </MenuItem>
              ) : null}
              {childClients.length ? <ListSubheader>Child clients</ListSubheader> : null}
              {childClients.map(item => (
                <MenuItem key={item.client_id} value={item.client_id.toString()}>
                  {item.name || "Unnamed client"} ({item.client_id})
                </MenuItem>
              ))}
            </Select>
          ) : null}

          <IconButton onClick={toggleSettings} color='inherit'><SettingsOutlinedIcon /></IconButton>
          <IconButton onClick={toggleHelp} color='inherit'><HelpOutlineIcon /></IconButton>
        </Toolbar>
      </AppBar>
    </Box>
  )
}

const StyledNavLink = ({ handleClose, url, label }) => <NavLink to={url} className="nav-link" onClick={handleClose}>{label}</NavLink>


const MenuItems = ({ handleClose }) => {

  const navigate = useNavigate()

  const { user, client, logoutUser: performLogout } = useContext(AppContext)

  const logoutUser = async () => {
    handleClose()
    await performLogout()
  }

  const menuItems = []

  if (client?.initialised) {
    menuItems.push(<MenuItem key="home" onClick={() => { navigate("/"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/" label="Home" /></MenuItem>)
    menuItems.push(<MenuItem key="templates" onClick={() => { navigate("/templates"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/templates" label="Pass Templates" /></MenuItem>)
    menuItems.push(<MenuItem key="create-template" onClick={() => { navigate("/create-template"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/create-templates" label="Create Template" /></MenuItem>)
    menuItems.push(<MenuItem key="active-passes" onClick={() => { navigate("/active-passes"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/active-passes" label="Active Passes" /></MenuItem>)

    menuItems.push(<MenuItem key="dashboard" onClick={() => { navigate("/download-apps"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/download-apps" label="Download Scan App" /></MenuItem>)

    if (user?.permissions.client_admin) {
      menuItems.push(<MenuItem key="statistics" onClick={() => { navigate("/statistics"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/statistics" label="Statistics" /></MenuItem>)
      menuItems.push(<MenuItem key="account-management" onClick={() => { navigate("/account-management"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/account-management" label="Account Management" /></MenuItem>)
      menuItems.push(<MenuItem key="billing" onClick={() => { navigate("/billing/invoices"); handleClose() }} className='link'><StyledNavLink handleClose={handleClose} url="/billing/invoices" label="Billing" /></MenuItem>)
    }

    menuItems.push(<MenuItem key="preferences" className="link" onClick={() => { toggleSettings(); handleClose() }}>Preferences</MenuItem>)
  }

  menuItems.push(<MenuItem key="support" onClick={() => { navigate("/support"); handleClose() }} className='link' ><StyledNavLink handleClose={handleClose} url="/support" label="Request Support" /></MenuItem>)
  menuItems.push(<MenuItem key="logout" onClick={logoutUser} className='link logout'>Logout</MenuItem>)

  return menuItems
}
